import { useCallback, useEffect, useMemo, useState } from "react";
import { useStore } from "../../../../hooks/useStore";
import User from "../../../../passapp-sdk/user";
import { VehicleInside } from "../../../../passapp-sdk/types/userTypes";
import {
  mapVehiclesInside,
  VehicleInsideRow,
} from "../../../../components/common/PL/Table/utils/vehicleInsideRow";
import { getCompanyNames } from "./getCompanyNames";

export interface UseVehiclesInsideOptions {
  search?: string;
  page?: number;
  limit?: number;
  /** Company IDs to filter by. */
  companies?: string[];
  /** Transport company IDs to filter by. */
  transportCompanies?: string[];
  /** En `false` no se pide nada: sirve para no traer la vista que no se está mostrando. */
  enabled?: boolean;
}

/**
 * Unidades dentro del complejo. Espeja a `usePeopleInside`, pero contra `getVehiclesInside`, que ya
 * devuelve una fila por unidad con el rol y el estado del chofer resueltos por el backend.
 *
 * Este endpoint no acepta ventana de tiempo ni `authorizationTags`, y el filtro por tipo de
 * carrocería tampoco existe: ese lo aplica la tabla sobre lo ya cargado.
 */
export const useVehiclesInside = ({
  search,
  page,
  limit,
  companies,
  transportCompanies,
  enabled = true,
}: UseVehiclesInsideOptions = {}) => {
  const [vehiclesInside, setVehiclesInside] = useState<VehicleInsideRow[]>([]);
  const [total, setTotal] = useState(0);
  /** `false` cuando la respuesta vino como array plano y no se sabe cuántas páginas hay. */
  const [hasTotal, setHasTotal] = useState(false);
  // Arranca en `true` cuando va a pedir: el fetch recién sale en el efecto, y con `false` el primer
  // render muestra el vacío ("no hay unidades") antes de que la request siquiera empiece.
  const [isLoading, setIsLoading] = useState(enabled);
  const [refetchKey, setRefetchKey] = useState(0);

  const { authStore } = useStore();
  const userSdk = useMemo(() => new User(), []);

  const fetchVehicles = useCallback(() => {
    if (!authStore.token || !enabled) return;

    setIsLoading(true);
    userSdk
      .getVehiclesInside(
        authStore.token,
        page,
        limit,
        search,
        undefined,
        companies,
        transportCompanies
      )
      .then((res) => {
        // El endpoint acepta page/limit pero la respuesta documentada es un array plano sin total;
        // se toleran las dos formas hasta que backend lo confirme.
        const payload = res?.data as VehicleInside[] | { data: VehicleInside[]; total: number };
        const list = Array.isArray(payload) ? payload : payload?.data ?? [];
        const paginated = !Array.isArray(payload) && typeof payload?.total === "number";

        setVehiclesInside(
          mapVehiclesInside(list, (companyId) =>
            getCompanyNames(companyId, authStore.companies || [])
          )
        );
        setHasTotal(paginated);
        setTotal(paginated ? (payload as { total: number }).total : list.length);
      })
      .catch((err) => {
        console.error("Error fetching vehicles inside:", err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [
    authStore.token,
    enabled,
    userSdk,
    authStore.companies,
    search,
    page,
    limit,
    companies,
    transportCompanies,
  ]);

  useEffect(() => {
    fetchVehicles();
  }, [fetchVehicles, refetchKey]);

  const refetch = useCallback(() => {
    setRefetchKey((prev) => prev + 1);
  }, []);

  return { vehiclesInside, isLoading, refetch, total, hasTotal };
};
